import { formatUkTimeHHmm, toUkDatetimeLocalValue } from './dateUtils';
import { toNumber } from './formatMoney';

type CabTypeRef = {
  id?: string;
  description?: string;
};

export function toDatetimeLocalValue(value: unknown): string {
  return toUkDatetimeLocalValue(value);
}

function toIntFlag(value: unknown, fallback = 0): number {
  if (value === true || value === 1 || value === '1') return 1;
  if (value === false || value === 0 || value === '0') return 0;
  return toNumber(value) ?? fallback;
}

function toText(value: unknown, fallback = ''): string {
  if (value == null) return fallback;
  return String(value);
}

/** Website bookings use startpoint/endposition; legacy rows may use manual* fields. */
export function journeyPickupAddress(booking: {
  manualPickupAddress?: unknown;
  startpoint?: unknown;
}): string {
  return toText(booking.manualPickupAddress) || toText(booking.startpoint);
}

export function journeyDropoffAddress(booking: {
  manualDropOffAddress?: unknown;
  endposition?: unknown;
}): string {
  return toText(booking.manualDropOffAddress) || toText(booking.endposition);
}

type JourneyWaypointBooking = {
  startpoint1?: unknown;
  startpoint2?: unknown;
  startpoint3?: unknown;
  endposition1?: unknown;
  endposition2?: unknown;
  endposition3?: unknown;
};

export type JourneyRouteStop = {
  kind: 'pickup' | 'via' | 'dropoff';
  viaIndex?: number;
  address: string;
};

function readViaAddresses(values: unknown[]): string[] {
  return values.map((value) => toText(value).trim()).filter(Boolean);
}

/** Via stops in drive order (pre-pickup vias, then post-pickup vias). */
export function journeyWaypoints(booking: JourneyWaypointBooking): string[] {
  return [
    ...readViaAddresses([
      booking.startpoint1,
      booking.startpoint2,
      booking.startpoint3,
    ]),
    ...readViaAddresses([
      booking.endposition1,
      booking.endposition2,
      booking.endposition3,
    ]),
  ];
}

/** Full route timeline for admin booking details. */
export function journeyRouteStops(
  booking: JourneyWaypointBooking & {
    manualPickupAddress?: unknown;
    startpoint?: unknown;
    manualDropOffAddress?: unknown;
    endposition?: unknown;
  },
): JourneyRouteStop[] {
  const preVias = readViaAddresses([
    booking.startpoint1,
    booking.startpoint2,
    booking.startpoint3,
  ]);
  const postVias = readViaAddresses([
    booking.endposition1,
    booking.endposition2,
    booking.endposition3,
  ]);
  const stops: JourneyRouteStop[] = [];

  preVias.forEach((address, index) => {
    stops.push({ kind: 'via', viaIndex: index + 1, address });
  });

  const pickup = journeyPickupAddress(booking).trim();
  if (pickup) {
    stops.push({ kind: 'pickup', address: pickup });
  }

  postVias.forEach((address, index) => {
    stops.push({ kind: 'via', viaIndex: preVias.length + index + 1, address });
  });

  const dropoff = journeyDropoffAddress(booking).trim();
  if (dropoff) {
    stops.push({ kind: 'dropoff', address: dropoff });
  }

  return stops;
}

export function journeyDistanceMiles(booking: {
  miles?: unknown;
  distance?: unknown;
}): number | null {
  const value = toNumber(booking.miles) ?? toNumber(booking.distance);
  return value != null && value > 0 ? value : null;
}

export function formatJourneyDistance(
  booking: { miles?: unknown; distance?: unknown },
  fallback = '—',
): string {
  const miles = journeyDistanceMiles(booking);
  if (miles == null) return fallback;
  const rounded = Number.isInteger(miles) ? String(miles) : miles.toFixed(1);
  return `${rounded} miles`;
}

/** Maps API booking payload (Prisma decimals, nested relations) into edit-form shape. */
export function normalizeBookingForForm<T extends object>(
  api: Record<string, unknown>,
  defaults: T,
): T {
  const cabType = api.cabType as CabTypeRef | undefined;
  const vehicleTypeId =
    toText(api.vehicleTypeId) ||
    toText(api.cabTypeId) ||
    toText(cabType?.id);

  const {
    cabType: _cabType,
    customer: _customer,
    driver: _driver,
    cab: _cab,
    payments: _payments,
    ...flat
  } = api;

  const pickupInstant =
    api.pickupDateTime ?? api.pickupTime ?? api.pickupDateWithTime;
  const pickupDate = pickupInstant ? new Date(String(pickupInstant)) : null;
  const pickupIso =
    pickupDate && !Number.isNaN(pickupDate.getTime())
      ? pickupDate.toISOString()
      : '';
  const pickupUkTime = pickupIso ? formatUkTimeHHmm(pickupIso) : '';

  return {
    ...defaults,
    ...(flat as Partial<T>),
    id: toText(api.id, toText((defaults as { id?: string }).id)),
    vehicleTypeId,
    vehicleTypeName: toText(api.vehicleTypeName) || toText(cabType?.description),
    pasengerName: toText(api.pasengerName),
    cellno: toText(api.cellno),
    email: toText(api.email),
    manualPickupAddress: journeyPickupAddress(api),
    manualDropOffAddress: journeyDropoffAddress(api),
    startpoint: toText(api.startpoint),
    startpoint1: toText(api.startpoint1),
    startpoint2: toText(api.startpoint2),
    startpoint3: toText(api.startpoint3),
    endposition: toText(api.endposition),
    endposition1: toText(api.endposition1),
    endposition2: toText(api.endposition2),
    endposition3: toText(api.endposition3),
    pickupPostalCode: toText(api.pickupPostalCode),
    pickupPostalCode1: toText(api.pickupPostalCode1),
    pickupPostalCode2: toText(api.pickupPostalCode2),
    pickupPostalCode3: toText(api.pickupPostalCode3),
    dropoffPostalCode: toText(api.dropoffPostalCode),
    dropoffPostalCode1: toText(api.dropoffPostalCode1),
    dropoffPostalCode2: toText(api.dropoffPostalCode2),
    dropoffPostalCode3: toText(api.dropoffPostalCode3),
    waitTime: toText(api.waitTime),
    driverId: toText(api.driverId) || toText((api.driver as { id?: string } | undefined)?.id),
    paymentMode: toText(api.paymentMode, 'cash'),
    status: toText(api.status, 'pending'),
    paymentStatus: toText(api.paymentStatus, 'pending'),
    additionalInfo: toText(api.additionalInfo),
    flightNo: toText(api.flightNo),
    arrivalFrom: toText(api.arrivalFrom),
    terminal: toText(api.terminal),
    doorNo: toText(api.doorNo),
    pickupDateTime: toDatetimeLocalValue(pickupInstant),
    pickupDateTimeStr: pickupIso,
    pickupTime: pickupIso,
    pickupDateWithTime: pickupIso,
    pickupTimeSpan: pickupUkTime,
    pickupTimeString: pickupUkTime,
    dropoffDateTime: toDatetimeLocalValue(api.dropoffDateTime),
    flightTime: toDatetimeLocalValue(api.flightTime),
    amount: toNumber(api.amount) ?? 0,
    calculatedfare: toNumber(api.calculatedfare) ?? 0,
    estimateFareNet: toNumber(api.estimateFareNet) ?? 0,
    luggagekg: toNumber(api.luggagekg) ?? 0,
    miles: toNumber(api.miles) ?? 0,
    noOfAdult: toNumber(api.noOfAdult) ?? 1,
    noOfChild: toNumber(api.noOfChild) ?? 0,
    noofPassenger: toNumber(api.noofPassenger) ?? 1,
    childSeat: toIntFlag(api.childSeat),
    disableSeat: toIntFlag(api.disableSeat),
    returnTrip: toIntFlag(api.returnTrip),
    meetGreet: toIntFlag(api.meetGreet),
  };
}

export function formatFormNumber(value: number | string | null | undefined): string {
  const num = toNumber(value);
  return String(num ?? 0);
}

/** Adults + children; falls back to stored total when parts are missing. */
export function bookingPassengerTotal(booking: {
  noOfAdult?: number | string | null;
  noOfChild?: number | string | null;
  noofPassenger?: number | string | null;
}): number {
  const adults = toNumber(booking.noOfAdult);
  const children = toNumber(booking.noOfChild) ?? 0;
  if (adults != null && adults >= 0) {
    return adults + children;
  }
  return toNumber(booking.noofPassenger) ?? 0;
}
